import React, { useState } from 'react';
import { BarChart2, LayoutDashboard, Users, Globe } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDashboardStats } from '@/hooks/admin/useDashboardStats';
import { Skeleton } from '@/components/ui/skeleton';
import { useCountry } from '@/contexts/country';
import CountryFlag from '@/components/country/CountryFlag';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTheme } from '@/hooks/use-theme';

const AdminDashboardContent = () => {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const { availableCountries } = useCountry();
  const { data: stats, isLoading, error } = useDashboardStats(selectedCountry);
  const isMobile = useIsMobile();
  const { theme } = useTheme();

  const StatCard = ({ 
    title, 
    description, 
    value, 
    icon: Icon 
  }: { 
    title: string; 
    description: string; 
    value: number | undefined; 
    icon: React.ElementType; 
  }) => (
    <Card className={isMobile ? "mb-4" : ""}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon className="h-5 w-5 text-nation-purple" />
          <span>{title}</span>
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <p className="text-2xl font-bold">{value?.toLocaleString()}</p>
        )}
      </CardContent>
    </Card>
  );

  if (error) {
    return (
      <div className="p-6 text-red-500">
        Error loading dashboard statistics. Please try again later.
      </div>
    );
  }

  return (
    <div className="py-4 md:py-6 space-y-4 md:space-y-6">
      <div className="max-w-6xl mx-auto">
        <div className={`flex ${isMobile ? "flex-col" : "justify-between"} items-start md:items-center mb-4 md:mb-6`}>
          <h2 className="text-xl md:text-3xl font-bold mb-4 md:mb-0 text-gray-800 dark:text-gray-100">
            Dashboard Overview
          </h2>
          <div className="w-full md:w-auto">
            <Select
              value={selectedCountry || ""}
              onValueChange={(value) => setSelectedCountry(value || null)}
            >
              <SelectTrigger 
                className={`${isMobile ? "w-full" : "w-[200px]"} border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200`}
              >
                <SelectValue 
                  placeholder="Filter by country" 
                />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                <SelectItem value="global" className="text-gray-800 dark:text-gray-200">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-700 dark:text-gray-300" />
                    <span>All Countries</span>
                  </div>
                </SelectItem>
                {availableCountries.map((country) => (
                  <SelectItem key={country.code} value={country.code} className="text-gray-800 dark:text-gray-200">
                    <div className="flex items-center gap-2">
                      <CountryFlag countryCode={country.code} className="h-4 w-6" />
                      <span>{country.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"} gap-4 md:gap-6 mb-6 md:mb-8`}>
          <StatCard
            title="Users"
            description={selectedCountry ? `Users in selected country` : "Total registered users"}
            value={stats?.userCount}
            icon={Users}
          />
          
          <StatCard
            title="Insights"
            description={selectedCountry ? `Published insights in country` : "Published insights"}
            value={stats?.insightCount}
            icon={BarChart2}
          />
          
          <StatCard
            title="Polls"
            description={selectedCountry ? `Active & completed polls in country` : "Active & completed polls"}
            value={stats?.pollCount}
            icon={LayoutDashboard}
          />
          
          <StatCard
            title="Countries"
            description="Active country profiles"
            value={stats?.activeCountriesCount}
            icon={Globe}
          />
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 md:p-6 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4 text-gray-800 dark:text-gray-100">Welcome to the Admin Dashboard</h3>
          <p className="text-gray-700 dark:text-gray-300 mb-3 md:mb-4 text-sm md:text-base">
            Use the sidebar navigation to manage users, countries, insights, and appearance settings.
          </p>
          <p className="text-gray-700 dark:text-gray-300 text-sm md:text-base">
            For help or support, please contact the system administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardContent;
